import { createSelector } from '@reduxjs/toolkit';
import { parseISO } from 'date-fns';
import { ascend, path, pathOr, prop, sort, values } from 'ramda';

import ooeConstants from '../constants';
import { dashboardErrorMessages } from '../util/customerErrorMessages';
import { formatShortDate } from '../util/format';
import { State } from './reducer';
import { DashboardOrder } from './schemas';
import { filterOrders, formatDisplayFields, mapOrderDetails } from './utils';

// Selectors
export const selectDashboard = (state: { dashboard: State }) => state.dashboard;

// Dashboard Table selectors
export const selectOrders = createSelector(selectDashboard, (dashboard) =>
  values(dashboard.orders).map((order) => formatDisplayFields(order)),
);

// Filter selectors
export const selectDashboardFilters = createSelector(selectDashboard, (dashboard) => dashboard.filters);
export const selectDashboardSearchValue = createSelector(
  selectDashboard,
  (dashboard) => dashboard.searchValue ?? '',
);
export const selectDashboardSearchQuery = createSelector(
  selectDashboard,
  (dashboard) => dashboard.searchQuery ?? '',
);
export const selectFilteredOrders = createSelector(
  selectOrders,
  selectDashboardFilters,
  selectDashboardSearchQuery,
  filterOrders,
);

// Sort selectors
export const selectDashboardSort = createSelector(selectDashboard, (dashboard) => dashboard.sort);

// Detail View Selectors
export const selectOrderToView = createSelector(selectDashboard, (dashboard) => {
  if (dashboard.orders[dashboard.orderToViewId]) {
    return mapOrderDetails(dashboard.orders[dashboard.orderToViewId]);
  }

  return {} as ReturnType<typeof mapOrderDetails>;
});
export const selectIsPOSOrder = createSelector(selectOrderToView, (order) => order.clientId === 'POS');
export const selectIsErrorStatus = createSelector(
  selectOrderToView,
  (order) => order.status === ooeConstants.ERROR,
);
export const selectIsReadyStatus = createSelector(
  selectOrderToView,
  (order) => order.status === ooeConstants.READY,
);
export const selectOrderLineItems = createSelector(selectOrderToView, (order) => order.lineItems ?? []);

const isRewardLineItem = (
  order: ReturnType<typeof selectOrderToView>,
  item: ReturnType<typeof selectOrderLineItems>[number],
) => !!order.processLoyalty && !!item.compValue && item.compValue > 0;

export const selectRewardDiscountTotal = createSelector(
  selectOrderToView,
  selectOrderLineItems,
  (order, lineItems) =>
    lineItems.reduce((total, item) => total + (isRewardLineItem(order, item) ? (item.compValue ?? 0) : 0), 0),
);

export const selectPromoFreeDiscountTotal = createSelector(
  selectOrderToView,
  selectOrderLineItems,
  (order, lineItems) =>
    lineItems.reduce(
      (total, item) => total + (!isRewardLineItem(order, item) && item.promoFree ? (item.compValue ?? 0) : 0),
      0,
    ),
);

// The subtotal returned by the API is already net of any reward / promo free discounts, so add them
// back to show the full price of the items before the discount lines below it.
export const selectSubtotalBeforeDiscounts = createSelector(
  selectOrderToView,
  selectRewardDiscountTotal,
  selectPromoFreeDiscountTotal,
  (order, rewardDiscountTotal, promoFreeDiscountTotal) =>
    (order.subTotalAmount ?? 0) + rewardDiscountTotal + promoFreeDiscountTotal,
);

// Detail View action selectors
export const selectPaymentEmailResent = createSelector(selectDashboard, (dashboard) => dashboard.emailSent);

// Order history selectors
export const selectOrderHistory = createSelector(selectOrderToView, (order) => {
  const orderHistory: Array<{ timestamp: string; event: string; sortableValue: number }> = [];
  let timestamps: DashboardOrder['timestamps'] = pathOr({}, ['timestamps'], order);
  if (order.fullError) {
    const errorTimestamp = { error: path(['fullError', 'createdAt'], order) };
    timestamps = { ...timestamps, ...errorTimestamp };
  }

  Object.entries(timestamps ?? {}).forEach(([k, v]) =>
    orderHistory.push({
      timestamp: formatShortDate(v),
      event:
        ooeConstants.GET_EVENT_MESSAGE(order)[k as keyof ReturnType<typeof ooeConstants.GET_EVENT_MESSAGE>],
      sortableValue: parseISO(v).valueOf(),
    }),
  );

  const byTime = ascend<{ sortableValue: number }>(prop('sortableValue'));
  return sort(byTime, orderHistory);
});

export const selectLastEndDate = createSelector(selectDashboard, (dashboard) => dashboard?.endDate);

// Loading selectors
export const selectOrdersAreLoading = createSelector(
  selectDashboard,
  (dashboard) => dashboard.loading.orders,
);
export const selectLookupOrderIsLoading = createSelector(
  selectDashboard,
  (dashboard) => dashboard.loading.orderDetails || dashboard.loading.customerDetails,
);
export const selectDashboardActionLoading = createSelector(
  selectDashboard,
  (dashboard) => dashboard.loading.cancelOrder || dashboard.loading.resendEmail,
);

export const selectDashboardPastOrderLoading = createSelector(
  selectDashboard,
  (dashboard) => dashboard?.loading?.loadMorePastOrders,
);

// Error selectors
export const selectDashboardErrors = createSelector(selectDashboard, (dashboard) => dashboard.error);
export const selectOrdersError = createSelector(selectDashboardErrors, (errors) => errors.orders);
export const selectExcelExtractionError = createSelector(
  selectDashboardErrors,
  (errors) => errors.excelExtraction,
);

export const selectExtractOrdersDataLoading = createSelector(
  selectDashboard,
  (dashboard) => dashboard?.loadingExcelAction,
);

export const selectExtractOrdersData = createSelector(
  selectDashboard,
  (dashboard) => dashboard?.exportExcelOrders,
);

export const selectAllowExcelDownload = createSelector(
  selectDashboard,
  (dashboard) => dashboard?.allowExcelDownload,
);

export const selectExportEntireDaysExcelError = createSelector(
  selectDashboard,
  (dashboard) => dashboard?.errorOccured && dashboardErrorMessages.GET_EXCEL_DATA_ERROR,
);

export const selectSpecificDays = createSelector(selectDashboard, (dashboard) => ({
  ...dashboard?.specificDays,
}));

export const selectTooLateToCancelOrEdit = createSelector(selectOrderToView, (orderToView) => {
  const { status } = orderToView;
  return status === ooeConstants.FINALIZED;
});

export const selectDetailViewErrors = createSelector(
  selectTooLateToCancelOrEdit,
  selectIsErrorStatus,
  selectDashboardErrors,
  selectIsPOSOrder,
  (tooLate, errorStatus, errors, isPOSOrder) => {
    let detailViewErrors = Object.values(errors);
    if (tooLate) {
      detailViewErrors.push(dashboardErrorMessages.TOO_LATE_TO_CANCEL);
    }
    if (errorStatus) {
      detailViewErrors.push(dashboardErrorMessages.ERROR_STATUS);
    }
    if (isPOSOrder) {
      detailViewErrors = [errors.orderDetails];
      detailViewErrors.push(dashboardErrorMessages.POS_ORDER);
    }
    return detailViewErrors;
  },
);
